import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Divider,
  Stack,
  Container,
  Fade,
  Tooltip,
  Badge,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ClockInIcon,
  Stop as ClockOutIcon,
  Pause as BreakStartIcon,
  PlayCircle as BreakEndIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  CloudUpload as CloudUploadIcon,
  Dashboard as DashboardIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { attendanceService } from '../services/attendanceService';
import { employeeService } from '../services/employeeService';
import { useAuth } from '../context/AuthContext';
import { Attendance, Employee } from '../types';
import { formatDate, formatTime, convertToPhilippineTime, convertFromPhilippineTime } from '../utils/dateUtils';

const AttendancePage: React.FC = () => {
  const { user, isEmployee } = useAuth();
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [openClockDialog, setOpenClockDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentAttendance, setCurrentAttendance] = useState<Partial<Attendance>>({});
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [clockType, setClockType] = useState<'in' | 'out' | 'break_start' | 'break_end'>('in');
  const [notes, setNotes] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [todayAttendance, setTodayAttendance] = useState<Attendance | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());
  const [searchName, setSearchName] = useState<string>('');
  const [searchDepartment, setSearchDepartment] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Set default date range to current month
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
          .toISOString()
          .split('T')[0];

        setStartDate(firstDay);
        setEndDate(lastDay);

        let attendanceData: any[];
        let employeesData: any[] = [];

        if (isEmployee() && user?.employee_id) {
          // For employees, only fetch their own attendance
          attendanceData = await attendanceService.getByEmployeeId(user.employee_id);
          employeesData = []; // Employees don't need to see other employees
          
          // Get today's attendance for the employee
          const todayStr = new Date().toISOString().split('T')[0];
          const todayRecord = attendanceData.find(att => att.date === todayStr);
          setTodayAttendance(todayRecord || null);
        } else {
          // For admin/HR/managers, fetch all data
          attendanceData = await attendanceService.getByDateRange(firstDay, lastDay);
          employeesData = await employeeService.getAll();
        }
        
        setAttendance(attendanceData);
        setEmployees(employeesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isEmployee, user?.employee_id]);



  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setCurrentAttendance({
      status: 'present',
      date: new Date().toISOString().split('T')[0],
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (record: Attendance) => {
    setDialogMode('edit');
    setCurrentAttendance({ ...record });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentAttendance({});
  };

  const handleOpenClockDialog = (type: 'in' | 'out' | 'break_start' | 'break_end') => {
    setClockType(type);
    setNotes('');
    setOpenClockDialog(true);
  };

  const handleCloseClockDialog = () => {
    setOpenClockDialog(false);
    setSelectedEmployee('');
    setNotes('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentAttendance(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setCurrentAttendance(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveAttendance = async () => {
    try {
      // Convert datetime-local format to Philippine timezone ISO string for database
      const formatDateTimeForDB = (dateTimeLocal: string | null | undefined): string | null => {
        if (!dateTimeLocal) return null;
        // Convert to Philippine timezone (UTC+8) before saving to database
        return convertToPhilippineTime(dateTimeLocal);
      };

      const formattedAttendance = {
        ...currentAttendance,
        clock_in: formatDateTimeForDB(currentAttendance.clock_in),
        clock_out: formatDateTimeForDB(currentAttendance.clock_out),
        break_start: formatDateTimeForDB(currentAttendance.break_start),
        break_end: formatDateTimeForDB(currentAttendance.break_end),
      };

      if (dialogMode === 'add') {
        await attendanceService.create(formattedAttendance as Omit<Attendance, 'id' | 'created_at' | 'updated_at'>);
      } else {
        if (currentAttendance.id) {
          await attendanceService.update(currentAttendance.id, formattedAttendance);
        }
      }

      // Refresh attendance list
      const attendanceData = await attendanceService.getByDateRange(startDate, endDate);
      setAttendance(attendanceData);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving attendance:', error);
    }
  };

  const handleDeleteAttendance = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this attendance record?')) {
      try {
        await attendanceService.delete(id);
        // Refresh attendance list
        const attendanceData = await attendanceService.getByDateRange(startDate, endDate);
        setAttendance(attendanceData);
      } catch (error) {
        console.error('Error deleting attendance:', error);
      }
    }
  };

  const handleClock = async () => {
    try {
      if (selectedEmployee) {
        if (clockType === 'in') {
          await attendanceService.clockIn(selectedEmployee, notes);
        } else if (clockType === 'out') {
          await attendanceService.clockOut(selectedEmployee);
        } else if (clockType === 'break_start') {
          await attendanceService.startBreak(selectedEmployee);
        } else if (clockType === 'break_end') {
          await attendanceService.endBreak(selectedEmployee);
        }

        // Refresh attendance list
        const attendanceData = await attendanceService.getByDateRange(startDate, endDate);
        setAttendance(attendanceData);
        handleCloseClockDialog();
      }
    } catch (error) {
      console.error('Error with attendance action:', error);
    }
  };

  // Employee clock actions
  const handleEmployeeClockIn = async () => {
    if (!user?.employee_id) return;
    try {
      await attendanceService.clockIn(user.employee_id);
      // Refresh today's attendance
      const attendanceData = await attendanceService.getByEmployeeId(user.employee_id);
      const today = new Date().toISOString().split('T')[0];
      const todayRecord = attendanceData.find(att => att.date === today);
      setTodayAttendance(todayRecord || null);
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error clocking in:', error);
    }
  };

  const handleEmployeeClockOut = async () => {
    if (!user?.employee_id) return;
    try {
      await attendanceService.clockOut(user.employee_id);
      // Refresh today's attendance
      const attendanceData = await attendanceService.getByEmployeeId(user.employee_id);
      const today = new Date().toISOString().split('T')[0];
      const todayRecord = attendanceData.find(att => att.date === today);
      setTodayAttendance(todayRecord || null);
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error clocking out:', error);
    }
  };

  const handleEmployeeStartBreak = async () => {
    if (!user?.id) return;
    try {
      await attendanceService.startBreak(user.id);
      // Refresh today's attendance
      const attendanceData = await attendanceService.getByEmployeeId(user.id);
      const today = new Date().toISOString().split('T')[0];
      const todayRecord = attendanceData.find(att => att.date === today);
      setTodayAttendance(todayRecord || null);
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error starting break:', error);
    }
  };

  const handleEmployeeEndBreak = async () => {
    if (!user?.id) return;
    try {
      await attendanceService.endBreak(user.id);
      // Refresh today's attendance
      const attendanceData = await attendanceService.getByEmployeeId(user.id);
      const today = new Date().toISOString().split('T')[0];
      const todayRecord = attendanceData.find(att => att.date === today);
      setTodayAttendance(todayRecord || null);
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error ending break:', error);
    }
  };

  const handleDateRangeChange = async () => {
    try {
      setLoading(true);
      const attendanceData = await attendanceService.getByDateRange(startDate, endDate);
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Excel Upload Functions
  const downloadExcelTemplate = () => {
    // Use actual employee IDs from the system
    const sampleEmployees = employees.slice(0, 2);
    const templateData = sampleEmployees.length > 0 ? [
      {
        'Employee ID': sampleEmployees[0]?.id || 'EMPLOYEE_ID_HERE',
        'Employee Name': sampleEmployees[0] ? `${sampleEmployees[0].first_name} ${sampleEmployees[0].last_name}` : 'Employee Name',
        'Date': '2024-01-15',
        'Clock In': '09:00',
        'Clock Out': '17:00',
        'Break Start': '12:00',
        'Break End': '13:00',
        'Status': 'present',
        'Notes': 'Regular work day'
      },
      {
        'Employee ID': sampleEmployees[1]?.id || 'EMPLOYEE_ID_HERE',
        'Employee Name': sampleEmployees[1] ? `${sampleEmployees[1].first_name} ${sampleEmployees[1].last_name}` : 'Employee Name',
        'Date': '2024-01-15',
        'Clock In': '08:30',
        'Clock Out': '16:30',
        'Break Start': '12:30',
        'Break End': '13:30',
        'Status': 'present',
        'Notes': 'Early start'
      }
    ] : [
      {
        'Employee ID': 'EMPLOYEE_ID_HERE',
        'Employee Name': 'Employee Name',
        'Date': '2024-01-15',
        'Clock In': '09:00',
        'Clock Out': '17:00',
        'Break Start': '12:00',
        'Break End': '13:00',
        'Status': 'present',
        'Notes': 'Regular work day'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance Template');
    
    // Add instructions sheet
    const instructions = [
      { 'Field': 'Employee ID', 'Description': 'Unique employee identifier from system (required)', 'Format': 'UUID format (copy from employee list)' },
      { 'Field': 'Employee Name', 'Description': 'Full name of employee (for reference only)', 'Format': 'Text' },
      { 'Field': 'Date', 'Description': 'Date of attendance (required)', 'Format': 'YYYY-MM-DD (e.g., 2024-01-15)' },
      { 'Field': 'Clock In', 'Description': 'Time employee clocked in', 'Format': 'HH:MM (e.g., 09:00)' },
      { 'Field': 'Clock Out', 'Description': 'Time employee clocked out', 'Format': 'HH:MM (e.g., 17:00)' },
      { 'Field': 'Break Start', 'Description': 'Time break started (optional)', 'Format': 'HH:MM (e.g., 12:00)' },
      { 'Field': 'Break End', 'Description': 'Time break ended (optional)', 'Format': 'HH:MM (e.g., 13:00)' },
      { 'Field': 'Status', 'Description': 'Attendance status (required)', 'Format': 'present, absent, late, half-day, incomplete' },
      { 'Field': 'Notes', 'Description': 'Additional notes (optional)', 'Format': 'Text' }
    ];
    
    // Add employee list for reference
    const employeeList = employees.map(emp => ({
      'Employee ID': emp.id,
      'Employee Name': `${emp.first_name} ${emp.last_name}`,
      'Position': emp.position,
      'Department': emp.department
    }));
    
    if (employeeList.length > 0) {
      const employeeWs = XLSX.utils.json_to_sheet(employeeList);
      XLSX.utils.book_append_sheet(wb, employeeWs, 'Employee List');
    }
    
    const instructionsWs = XLSX.utils.json_to_sheet(instructions);
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
    
    XLSX.writeFile(wb, 'Attendance_Upload_Template.xlsx');
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const result = await processExcelData(jsonData);
        setUploadDialogOpen(false);
        
        // Refresh attendance data
        const attendanceData = await attendanceService.getByDateRange(startDate, endDate);
        setAttendance(attendanceData);
        
        // Show detailed summary
        let summary = `Upload completed!\nNew records: ${result.savedCount}\nUpdated records: ${result.updateCount}`;
        if (result.errors.length > 0) {
          summary += `\nProcessing errors: ${result.errors.length}`;
        }
        if (result.saveErrors.length > 0) {
          summary += `\nSave errors: ${result.saveErrors.length}`;
        }
        
        alert(summary);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        alert('Error processing Excel file. Please check the format and try again.');
      } finally {
        setUploading(false);
        // Reset file input
        event.target.value = '';
      }
    };
    
    reader.readAsArrayBuffer(file);
  };

  const processExcelData = async (data: any[]) => {
    const processedRecords = [];
    const errors = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      try {
        // Find employee by ID
        const employee = employees.find(emp => emp.id === row['Employee ID']);
        if (!employee) {
          errors.push(`Row ${i + 1}: Employee ID '${row['Employee ID']}' not found`);
          continue;
        }

        // Validate required fields
        if (!row['Date']) {
          errors.push(`Row ${i + 1}: Date is required`);
          continue;
        }

        if (!row['Status']) {
          errors.push(`Row ${i + 1}: Status is required`);
          continue;
        }

        // Validate status value
        const validStatuses = ['present', 'absent', 'late', 'half-day', 'incomplete'];
        const status = row['Status'].toString().toLowerCase().trim();
        if (!validStatuses.includes(status)) {
          errors.push(`Row ${i + 1}: Invalid status '${row['Status']}'. Must be one of: ${validStatuses.join(', ')}`);
          continue;
        }

        // Format date - handle Excel date formats properly
        let date;
        if (typeof row['Date'] === 'number') {
          // Excel serial date number
          const excelDate = new Date((row['Date'] - 25569) * 86400 * 1000);
          date = excelDate.toISOString().split('T')[0];
        } else if (typeof row['Date'] === 'string') {
          // String date format
          const parsedDate = new Date(row['Date']);
          if (isNaN(parsedDate.getTime())) {
            errors.push(`Row ${i + 1}: Invalid date format '${row['Date']}'. Use YYYY-MM-DD format.`);
            continue;
          }
          date = parsedDate.toISOString().split('T')[0];
        } else {
          errors.push(`Row ${i + 1}: Invalid date format. Use YYYY-MM-DD format.`);
          continue;
        }
        
        // Format times (convert HH:MM to full datetime)
        const formatTime = (timeValue: any, date: string) => {
          if (!timeValue) return null;
          
          let timeStr;
          if (typeof timeValue === 'number') {
            // Excel time serial number (fraction of a day)
            const totalMinutes = Math.round(timeValue * 24 * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          } else if (typeof timeValue === 'string') {
            timeStr = timeValue;
          } else {
            return null;
          }
          
          // Validate time format
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          if (!timeRegex.test(timeStr)) {
            return null;
          }
          
          const [hours, minutes] = timeStr.split(':');
          return `${date}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
        };

        const clockIn = formatTime(row['Clock In'], date);
        const clockOut = formatTime(row['Clock Out'], date);
        const breakStart = formatTime(row['Break Start'], date);
        const breakEnd = formatTime(row['Break End'], date);

        const attendanceRecord = {
          employee_id: employee.id,
          date: date,
          clock_in: clockIn,
          clock_out: clockOut,
          break_start: breakStart,
          break_end: breakEnd,
          status: status,
          notes: row['Notes'] ? row['Notes'].toString() : '',
          // Calculate total hours worked
          total_hours_worked: calculateHoursWorked(clockIn, clockOut, breakStart, breakEnd),
          regular_hours: 0, // Will be calculated by the service
          overtime_hours: 0 // Will be calculated by the service
        };

        processedRecords.push(attendanceRecord);
      } catch (error) {
        console.error(`Error processing row ${i + 1}:`, error);
        errors.push(`Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
      }
    }

    if (errors.length > 0) {
      console.log('Processing errors:', errors);
      alert(`Processing completed with errors:\n${errors.join('\n')}`);
    }

    // Save processed records
    let savedCount = 0;
    let updateCount = 0;
    const saveErrors = [];
    
    for (const record of processedRecords) {
      try {
        // Check if record already exists for this employee and date
        const existingRecord = attendance.find(
          att => att.employee_id === record.employee_id && att.date === record.date
        );

        if (existingRecord) {
          // Update existing record
          await attendanceService.update(existingRecord.id, record);
          updateCount++;
        } else {
          // Create new record
          await attendanceService.create(record as Omit<Attendance, 'id' | 'created_at' | 'updated_at'>);
          savedCount++;
        }
      } catch (error) {
        console.error('Error saving attendance record:', error, record);
        saveErrors.push(`Failed to save record for employee ${record.employee_id} on ${record.date}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    return { processedRecords, errors, saveErrors, savedCount, updateCount };
  };

  const calculateHoursWorked = (
    clockIn: string | null,
    clockOut: string | null,
    breakStart: string | null,
    breakEnd: string | null
  ): number => {
    if (!clockIn || !clockOut) return 0;

    const clockInTime = new Date(clockIn).getTime();
    const clockOutTime = new Date(clockOut).getTime();
    
    let totalMinutes = (clockOutTime - clockInTime) / (1000 * 60);
    
    // Subtract break time if both break start and end are provided
    if (breakStart && breakEnd) {
      const breakStartTime = new Date(breakStart).getTime();
      const breakEndTime = new Date(breakEnd).getTime();
      const breakMinutes = (breakEndTime - breakStartTime) / (1000 * 60);
      totalMinutes -= breakMinutes;
    }
    
    return Math.max(0, totalMinutes / 60); // Convert to hours
  };





  // Handle accordion expansion
  const handleAccordionChange = (employeeId: string) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedEmployees(newExpanded);
  };

  // Group attendance records by employee with filtering
  const groupAttendanceByEmployee = () => {
    const grouped: { [key: string]: { employee: Employee; records: Attendance[] } } = {};
    
    attendance.forEach(record => {
      const employee = employees.find(emp => emp.id === record.employee_id);
      if (employee) {
        // Apply name filter
        const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
        const nameMatches = searchName === '' || fullName.includes(searchName.toLowerCase());
        
        // Apply department filter
        const departmentMatches = searchDepartment === '' || 
          employee.department.toLowerCase().includes(searchDepartment.toLowerCase());
        
        // Only include if both filters match
        if (nameMatches && departmentMatches) {
          if (!grouped[record.employee_id]) {
            grouped[record.employee_id] = {
              employee,
              records: []
            };
          }
          grouped[record.employee_id].records.push(record);
        }
      }
    });
    
    // Sort records within each group by date (newest first)
    Object.values(grouped).forEach(group => {
      group.records.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    });
    
    return grouped;
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchName('');
    setSearchDepartment('');
  };



  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '60vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" color="text.secondary">
            Loading attendance data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <ScheduleIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              {isEmployee() ? 'My Attendance' : 'Attendance Management'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isEmployee() 
                ? 'Track your daily attendance and work hours'
                : 'Monitor and manage employee attendance records'
              }
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Employee Clock Interface */}
      {isEmployee() && (
        <Fade in timeout={800}>
          <Card 
            sx={{ 
              mb: 4, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
            }}
          >
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                  <TimerIcon />
                </Avatar>
              }
              title={
                <Typography variant="h5" fontWeight="bold">
                  Today's Attendance
                </Typography>
              }
              subheader={
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {formatDate(new Date().toISOString().split('T')[0])}
                </Typography>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                 <Grid size={{ xs: 12, md: 8 }}>
                   {todayAttendance ? (
                     <Grid container spacing={2}>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                          <ClockInIcon sx={{ fontSize: 32, mb: 1 }} />
                          <Typography variant="caption" display="block">Clock In</Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {todayAttendance.clock_in ? formatTime(todayAttendance.clock_in) : '--:--'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                          <ClockOutIcon sx={{ fontSize: 32, mb: 1 }} />
                          <Typography variant="caption" display="block">Clock Out</Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {todayAttendance.clock_out ? formatTime(todayAttendance.clock_out) : '--:--'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                          <BreakStartIcon sx={{ fontSize: 32, mb: 1 }} />
                          <Typography variant="caption" display="block">Break Start</Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {todayAttendance.break_start ? formatTime(todayAttendance.break_start) : '--:--'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 3 }}>
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                          <BreakEndIcon sx={{ fontSize: 32, mb: 1 }} />
                          <Typography variant="caption" display="block">Break End</Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {todayAttendance.break_end ? formatTime(todayAttendance.break_end) : '--:--'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                          <Typography variant="body1">Status:</Typography>
                          <Chip
                            icon={todayAttendance.status === 'present' ? <CheckCircleIcon /> : 
                                  todayAttendance.status === 'late' ? <WarningIcon /> : <ErrorIcon />}
                            label={todayAttendance.status.toUpperCase()}
                            sx={{
                              bgcolor: todayAttendance.status === 'present' ? 'success.main' : 
                                      todayAttendance.status === 'late' ? 'warning.main' : 'error.main',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                          {todayAttendance.total_hours_worked && (
                            <Chip
                              icon={<TrendingUpIcon />}
                              label={`${todayAttendance.total_hours_worked.toFixed(1)}h worked`}
                              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert 
                      severity="info" 
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.1)', 
                        color: 'white',
                        '& .MuiAlert-icon': { color: 'white' }
                      }}
                    >
                      No attendance record for today. Please clock in to start tracking your time.
                    </Alert>
                  )}
                </Grid>
                 <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', justifyContent: 'center' }}>
                    {!todayAttendance?.clock_in && (
                      <Tooltip title="Start your workday">
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<ClockInIcon />}
                          onClick={handleEmployeeClockIn}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            borderRadius: 2,
                            py: 1.5
                          }}
                        >
                          Clock In
                        </Button>
                      </Tooltip>
                    )}
                    {todayAttendance?.clock_in && !todayAttendance?.clock_out && (
                      <Tooltip title="End your workday">
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<ClockOutIcon />}
                          onClick={handleEmployeeClockOut}
                          sx={{
                            bgcolor: 'error.main',
                            '&:hover': { bgcolor: 'error.dark' },
                            borderRadius: 2,
                            py: 1.5
                          }}
                        >
                          Clock Out
                        </Button>
                      </Tooltip>
                    )}
                    {todayAttendance?.clock_in && !todayAttendance?.break_start && (
                      <Tooltip title="Take a break">
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<BreakStartIcon />}
                          onClick={handleEmployeeStartBreak}
                          sx={{
                            borderColor: 'rgba(255,255,255,0.5)',
                            color: 'white',
                            '&:hover': { 
                              borderColor: 'white',
                              bgcolor: 'rgba(255,255,255,0.1)'
                            },
                            borderRadius: 2,
                            py: 1.5
                          }}
                        >
                          Start Break
                        </Button>
                      </Tooltip>
                    )}
                    {todayAttendance?.break_start && !todayAttendance?.break_end && (
                      <Tooltip title="Resume work">
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<BreakEndIcon />}
                          onClick={handleEmployeeEndBreak}
                          sx={{
                            bgcolor: 'success.main',
                            '&:hover': { bgcolor: 'success.dark' },
                            borderRadius: 2,
                            py: 1.5
                          }}
                        >
                          End Break
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Admin Controls */}
      {!isEmployee() && (
        <Fade in timeout={1000}>
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                 <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <CloudUploadIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Data Management</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Import and export attendance data
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Tooltip title="Download Excel template for bulk upload">
                      <Button
                        variant="outlined"
                        startIcon={<ExportIcon />}
                        onClick={downloadExcelTemplate}
                        sx={{ borderRadius: 2 }}
                      >
                        Download Template
                      </Button>
                    </Tooltip>
                    <Tooltip title="Upload attendance data from Excel">
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ borderRadius: 2 }}
                      >
                        Upload Excel
                      </Button>
                    </Tooltip>
                  </Stack>
                </Grid>
                 <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <DashboardIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">Quick Actions</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Manage employee attendance
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Tooltip title="Clock in employee">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClockInIcon />}
                        onClick={() => handleOpenClockDialog('in')}
                        sx={{ borderRadius: 2 }}
                      >
                        Clock In
                      </Button>
                    </Tooltip>
                    <Tooltip title="Clock out employee">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClockOutIcon />}
                        onClick={() => handleOpenClockDialog('out')}
                        sx={{ borderRadius: 2 }}
                      >
                        Clock Out
                      </Button>
                    </Tooltip>
                    <Tooltip title="Start employee break">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<BreakStartIcon />}
                        onClick={() => handleOpenClockDialog('break_start')}
                        sx={{ borderRadius: 2 }}
                      >
                        Start Break
                      </Button>
                    </Tooltip>
                    <Tooltip title="End employee break">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<BreakEndIcon />}
                        onClick={() => handleOpenClockDialog('break_end')}
                        sx={{ borderRadius: 2 }}
                      >
                        End Break
                      </Button>
                    </Tooltip>
                    <Tooltip title="Add new attendance record">
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                        sx={{ borderRadius: 2 }}
                      >
                        Add Record
                      </Button>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Date Range Filter - Only for admin users */}
      {!isEmployee() && (
        <Fade in timeout={1200}>
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <FilterIcon />
                </Avatar>
              }
              title={
                <Typography variant="h6" fontWeight="bold">
                  Filter Attendance Records
                </Typography>
              }
              subheader={
                <Typography variant="body2" color="text.secondary">
                  Filter by date range, employee name, or department
                </Typography>
              }
            />
            <CardContent>
              <Grid container spacing={3} alignItems="end">
                 <Grid size={{ xs: 12, sm: 3 }}>
                   <TextField
                     label="Start Date"
                     type="date"
                     fullWidth
                     value={startDate}
                     onChange={(e) => setStartDate(e.target.value)}
                     InputLabelProps={{ shrink: true }}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: 2
                       }
                     }}
                   />
                 </Grid>
                 <Grid size={{ xs: 12, sm: 3 }}>
                   <TextField
                     label="End Date"
                     type="date"
                     fullWidth
                     value={endDate}
                     onChange={(e) => setEndDate(e.target.value)}
                     InputLabelProps={{ shrink: true }}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: 2
                       }
                     }}
                   />
                 </Grid>
                 <Grid size={{ xs: 12, sm: 2 }}>
                   <TextField
                     label="Search Name"
                     fullWidth
                     value={searchName}
                     onChange={(e) => setSearchName(e.target.value)}
                     placeholder="Employee name"
                     InputProps={{
                       startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                     }}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: 2
                       }
                     }}
                   />
                 </Grid>
                 <Grid size={{ xs: 12, sm: 2 }}>
                   <TextField
                     label="Department"
                     fullWidth
                     value={searchDepartment}
                     onChange={(e) => setSearchDepartment(e.target.value)}
                     placeholder="Department"
                     InputProps={{
                       startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                     }}
                     sx={{
                       '& .MuiOutlinedInput-root': {
                         borderRadius: 2
                       }
                     }}
                   />
                 </Grid>
                 <Grid size={{ xs: 6, sm: 1.5 }}>
                   <Button
                     variant="outlined"
                     onClick={handleClearFilters}
                     fullWidth
                     size="large"
                     startIcon={<ClearIcon />}
                     sx={{ 
                       borderRadius: 2,
                       py: 1.5,
                       borderColor: 'grey.400',
                       color: 'grey.600',
                       '&:hover': {
                         borderColor: 'grey.600',
                         bgcolor: 'grey.50'
                       },
                       minWidth: '120px'
                     }}
                   >
                     Clear
                   </Button>
                 </Grid>
                 <Grid size={{ xs: 6, sm: 1.5 }}>
                   <Button
                     variant="contained"
                     onClick={handleDateRangeChange}
                     fullWidth
                     size="large"
                     startIcon={<CalendarIcon />}
                     sx={{ 
                       borderRadius: 2,
                       py: 1.5,
                       background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                       minWidth: '140px'
                     }}
                   >
                     Apply Filter
                   </Button>
                 </Grid>
               </Grid>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Attendance Table */}
      <Fade in timeout={1400}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <CardHeader
            avatar={
              <Badge badgeContent={attendance.length} color="primary">
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <PersonIcon />
                </Avatar>
              </Badge>
            }
            title={
              <Typography variant="h6" fontWeight="bold">
                Attendance Records
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                {attendance.length} records found
              </Typography>
            }
          />
          <Divider />
          <Box sx={{ p: 2 }}>
            {Object.entries(groupAttendanceByEmployee()).map(([employeeId, group]) => (
              <Accordion
                key={employeeId}
                expanded={expandedEmployees.has(employeeId)}
                onChange={() => handleAccordionChange(employeeId)}
                sx={{ 
                  mb: 2, 
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'grey.50',
                    borderRadius: '8px 8px 0 0',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2
                    }
                  }}
                >
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    {group.employee.first_name[0]}{group.employee.last_name[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {group.employee.first_name} {group.employee.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.employee.department} - {group.employee.position}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${group.records.length} records`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.25' }}>
                          <TableCell>Date</TableCell>
                          <TableCell align="center">Clock In</TableCell>
                          <TableCell align="center">Clock Out</TableCell>
                          <TableCell align="center">Break Start</TableCell>
                          <TableCell align="center">Break End</TableCell>
                          <TableCell align="center">Total Hours</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.records.map((record) => (
                          <TableRow key={record.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {formatDate(record.date)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={<ClockInIcon />}
                                label={record.clock_in ? formatTime(record.clock_in) : '--:--'}
                                variant={record.clock_in ? 'filled' : 'outlined'}
                                color={record.clock_in ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={<ClockOutIcon />}
                                label={record.clock_out ? formatTime(record.clock_out) : '--:--'}
                                variant={record.clock_out ? 'filled' : 'outlined'}
                                color={record.clock_out ? 'error' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color={record.break_start ? 'text.primary' : 'text.secondary'}>
                                {record.break_start ? formatTime(record.break_start) : '--:--'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color={record.break_end ? 'text.primary' : 'text.secondary'}>
                                {record.break_end ? formatTime(record.break_end) : '--:--'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={<TimerIcon />}
                                label={record.total_hours_worked ? `${record.total_hours_worked.toFixed(1)}h` : '0h'}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={
                                  record.status === 'present' ? <CheckCircleIcon /> :
                                  record.status === 'late' ? <WarningIcon /> :
                                  record.status === 'half-day' ? <InfoIcon /> : <ErrorIcon />
                                }
                                label={record.status.toUpperCase()}
                                color={
                                  record.status === 'present' ? 'success' :
                                  record.status === 'late' ? 'warning' :
                                  record.status === 'half-day' ? 'info' : 'error'
                                }
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {record.notes || 'No notes'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <Tooltip title="Edit record">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenEditDialog(record)}
                                    sx={{ 
                                      bgcolor: 'primary.50',
                                      '&:hover': { bgcolor: 'primary.100' }
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete record">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteAttendance(record.id)}
                                    sx={{ 
                                      bgcolor: 'error.50',
                                      '&:hover': { bgcolor: 'error.100' }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
            
            {Object.keys(groupAttendanceByEmployee()).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No attendance records found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your date range or add some attendance records
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Fade>

      {/* Add/Edit Attendance Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add Attendance Record' : 'Edit Attendance Record'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <FormControl sx={{ width: 'calc(50% - 8px)' }}>
              <InputLabel>Employee</InputLabel>
              <Select
                name="employee_id"
                value={currentAttendance.employee_id || ''}
                onChange={handleSelectChange}
                label="Employee"
                required
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.first_name} ${employee.last_name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="date"
              label="Date"
              type="date"
              value={currentAttendance.date || ''}
              onChange={handleInputChange}
              required
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <TextField
              name="clock_in"
              label="Clock In Time"
              type="datetime-local"
              value={currentAttendance.clock_in ? convertFromPhilippineTime(currentAttendance.clock_in) : ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <TextField
              name="clock_out"
              label="Clock Out Time"
              type="datetime-local"
              value={currentAttendance.clock_out ? convertFromPhilippineTime(currentAttendance.clock_out) : ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <TextField
              name="break_start"
              label="Break Start Time"
              type="datetime-local"
              value={currentAttendance.break_start ? convertFromPhilippineTime(currentAttendance.break_start) : ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <TextField
              name="break_end"
              label="Break End Time"
              type="datetime-local"
              value={currentAttendance.break_end ? convertFromPhilippineTime(currentAttendance.break_end) : ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 'calc(50% - 8px)' }}
            />
            <FormControl sx={{ width: 'calc(50% - 8px)' }}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={currentAttendance.status || 'present'}
                onChange={handleSelectChange}
                label="Status"
                required
              >
                <MenuItem value="present">Present</MenuItem>
                <MenuItem value="absent">Absent</MenuItem>
                <MenuItem value="late">Late</MenuItem>
                <MenuItem value="half-day">Half Day</MenuItem>
              </Select>
            </FormControl>
            <TextField
              name="notes"
              label="Notes"
              value={currentAttendance.notes || ''}
              onChange={handleInputChange}
              multiline
              rows={2}
              sx={{ width: 'calc(50% - 8px)' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveAttendance} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clock In/Out Dialog */}
      <Dialog open={openClockDialog} onClose={handleCloseClockDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {clockType === 'in' ? 'Clock In' : 
           clockType === 'out' ? 'Clock Out' :
           clockType === 'break_start' ? 'Start Break' : 'End Break'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Employee</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value as string)}
                label="Employee"
                required
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.first_name} ${employee.last_name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClockDialog}>Cancel</Button>
          <Button onClick={handleClock} variant="contained">
            {clockType === 'in' ? 'Clock In' : 
             clockType === 'out' ? 'Clock Out' :
             clockType === 'break_start' ? 'Start Break' : 'End Break'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Excel File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please upload an Excel file with attendance data. The file should contain columns for Employee ID, Date, Clock In, Clock Out, Break Start, Break End, and Status.
            </Typography>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              style={{ width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
            {uploading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Processing Excel file...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AttendancePage;